import Typography from "@mui/material/Typography"
import { observer } from "mobx-react-lite"
import { useRouterStore } from "features/main/main-router/org-guard/RouterStore"
import useStore from "reusable/mobx-store-utils"
import ProposalsEditStore, { ProposalsEditStoreProvider, useProposalsEditStore } from "./ProposalsEditStore"
import { AppBarContainerCloseButton } from "reusable/mui/app-bar-container/AppBarContainerCloseButton"
import StepperFullscreen from "features/gap-components/stepper/stepper-fullscreen/StepperFullscreen"
import ProposalsEditRecipients from "./step-1-recipients/ProposalsEditRecipients"
import ProposalsEditServices from "./step-2-services/ProposalsEditServices"
import ProposalsEditPayment from "./step-3-payment/ProposalsEditPayment"
import ProposalsEditDetails from "./step-4-details/ProposalsEditDetails"
import ProposalsEditReview from "./step-5-review-and-send/ProposalsEditReview"
import useInitialMount from "reusable/hooks/useInitialMount"
import SkeletonLoader from "reusable/mui/skeleton/SkeletonLoader"
import { ServiceStore, ServiceStoreProvider } from "features/services/ServiceStore"
import ProposalsEditRecipientsStore, { ProposalsEditRecipientsStoreProvider } from "./step-1-recipients/ProposalsEditRecipientsStore"
import ProposalsEditReviewSidebar from "./step-5-review-and-send/ProposalsEditReviewSidebar"
import ProposalsEditReviewStore, { ProposalsEditReviewStoreProvider } from "./step-5-review-and-send/ProposalsEditReviewStore"
import { useProposalsViewStore } from "../ProposalsViewStore"
import { notNull } from "@gap/utils/utils"
import { useEffect } from "react"
import { useCacheStore } from "cache/CacheStore"
import { useOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore"

export default observer(function ProposalsEdit() {
   const routerStore = useRouterStore()
   const orgGuardStore = useOrgGuardStore()
   const serviceStore = useStore(sp => new ServiceStore(sp), { orgGuardStore })
   const proposalsViewStore = useProposalsViewStore()
   const proposal = notNull(proposalsViewStore.proposal)
   const cacheStore = useCacheStore()
   const proposalsEditStore = useStore(sp => new ProposalsEditStore(sp), { routerStore, proposal, serviceStore, proposalsViewStore, cacheStore, orgGuardStore })
   const proposalsEditRecipientsStore = useStore(sp => new ProposalsEditRecipientsStore(sp), { proposalsEditStore })
   const proposalsEditReviewStore = useStore(sp => new ProposalsEditReviewStore(sp), { proposalsEditStore, proposalsEditRecipientsStore })

   const closeButton = <AppBarContainerCloseButton callback={proposalsEditStore.navigateToProposals} key="close-button" />

   useInitialMount(() => {
      proposalsEditRecipientsStore.fetch()
   })

   useEffect(() => {
      if (proposalsEditStore.storeRef.store) {
         proposalsEditStore.storeRef.store.changeStep(proposal.editingStepIndex)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Prevent re-fire when stepIndex changes
   }, [proposalsEditStore.storeRef])

   return <ProposalsEditStoreProvider store={proposalsEditStore}>
      <ProposalsEditRecipientsStoreProvider store={proposalsEditRecipientsStore}>
         <ProposalsEditReviewStoreProvider store={proposalsEditReviewStore}>
            <ServiceStoreProvider store={serviceStore}>
               <SkeletonLoader loading={proposalsEditStore.loader.isLoading || !proposalsEditStore.proposal}>
                  <StepperFullscreen
                     storeRef={proposalsEditStore.storeRef}
                     steps={[
                        {
                           id: 'recipients',
                           title: 'Recipients',
                           content: <ProposalsEditRecipients />,
                        },
                        {
                           id: 'services',
                           title: 'Services',
                           content: <ProposalsEditServices />,
                        },
                        {
                           id: 'payment',
                           title: 'Payment',
                           content: <ProposalsEditPayment />,
                        },
                        {
                           id: 'details',
                           title: 'Details',
                           content: <ProposalsEditDetails />,
                        },
                        {
                           id: 'review-and-send',
                           title: 'Review & send',
                           content: <ProposalsEditReview />,
                           sidebar: <ProposalsEditReviewSidebar />,
                           hideNavigation: true,
                        },
                     ]}
                     appBarProps={{
                        title: proposalsEditStore.appBarTitle,
                        rightButtons: [<LastSaved key="last-saved-component" />, closeButton],
                     }}
                     stepMustBeMarkedAsDoneBeforeContinue={true}
                     continueButtonIsLoading={proposalsEditStore.jobQueue.isRunning}
                     onStepChange={proposalsEditStore.onStepChange}
                  />
               </SkeletonLoader>
            </ServiceStoreProvider>
         </ProposalsEditReviewStoreProvider>
      </ProposalsEditRecipientsStoreProvider>
   </ProposalsEditStoreProvider>
})

const LastSaved = observer(function LastSaved() {
   const proposalsEditStore = useProposalsEditStore()
   return <Typography className="auto-save" sx={{ mt: 1 }} key="last-saved">{proposalsEditStore.lastSaved}</Typography>
})
